
import { supabase } from '@/lib/supabase';
import { computeAdjustedEstimateTotalAsync } from '@/utils/estimateTotalCalculation';

export interface InvoiceExportData {
  invoice: any;
  lineItems: any[];
  customer: any;
  company: any;
  userProfile: any;
  documentDefaults: any;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
}

export const fetchInvoiceExportData = async (invoiceId: string): Promise<InvoiceExportData> => {
  try {
    // Fetch invoice with customer and estimates data including total_invoiced
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select(`
        *,
      customers(
          name, last_name, customer_type, business_name, business_phone, business_email, vat_number,
          street_address, suburb, city, province_state, postal_code, cell_numbers, emails
        ),
        estimates(
          id, estimate_number, title, total_invoiced, created_at, estimate_date, expires_at, validity_period
        )
      `)
      .eq('id', invoiceId)
      .single();

    if (invoiceError) throw invoiceError;

    // If we have an estimate, compute the adjusted total using centralized utility
    let estimateTotal = 0;
    if (invoice.estimates?.id) {
      // Fetch estimate with adjustment_metadata
      const { data: estimateData } = await supabase
        .from('estimates')
        .select('main_version_id, adjustment_metadata')
        .eq('id', invoice.estimates.id)
        .single();

      const mainVersionId = estimateData?.main_version_id;
      const adjustmentMetadata = estimateData?.adjustment_metadata as any;
      
      // Get line items (from main version snapshot or current)
      let lineItemsForCalc: any[] = [];

      if (mainVersionId) {
        const { data: mainVersion } = await supabase
          .from('estimate_versions')
          .select('line_items_snapshot')
          .eq('id', mainVersionId)
          .maybeSingle();

        if (mainVersion?.line_items_snapshot && Array.isArray(mainVersion.line_items_snapshot) && mainVersion.line_items_snapshot.length > 0) {
          lineItemsForCalc = (mainVersion.line_items_snapshot as any[]).map((item: any) => ({
            ...item,
            total_cost_snapshot: item.total_cost_snapshot ?? item.total_cost ?? 0,
            quantity_in_base_units: item.quantity_in_base_units ?? item.quantity ?? 0,
          }));
        }
      }

      if (lineItemsForCalc.length === 0) {
        const { data: currentLineItems } = await supabase
          .from('estimate_line_items')
          .select('*')
          .eq('estimate_id', invoice.estimates.id);
        lineItemsForCalc = currentLineItems || [];
      }

      const result = await computeAdjustedEstimateTotalAsync(lineItemsForCalc, adjustmentMetadata);
      estimateTotal = result.adjustedSubtotal;
    }

    // Calculate total paid by project using actual payments from invoice_payments table
    // (EXCLUDING current invoice to avoid double counting)
    let totalPaidByProject = 0;
    if (invoice.estimates?.id) {
      // Get all invoice IDs for this estimate (excluding current invoice)
      const { data: projectInvoices, error: projectInvoicesError } = await supabase
        .from('invoices')
        .select('id')
        .eq('estimate_id', invoice.estimates.id)
        .neq('id', invoiceId);

      if (!projectInvoicesError && projectInvoices && projectInvoices.length > 0) {
        const invoiceIds = projectInvoices.map(inv => inv.id);
        
        // Sum actual payments from invoice_payments table
        const { data: payments, error: paymentsError } = await supabase
          .from('invoice_payments')
          .select('payment_amount, is_cancelled')
          .in('invoice_id', invoiceIds);

        if (!paymentsError && payments) {
          // Only count non-cancelled payments
          totalPaidByProject = payments
            .filter(p => !p.is_cancelled)
            .reduce((sum, p) => sum + (p.payment_amount || 0), 0);
        }
      }
    }

    // Create enhanced invoice with estimates that includes total_amount and total_paid
    const enhancedInvoice = {
      ...invoice,
      estimates: invoice.estimates ? {
        ...invoice.estimates,
        total_amount: estimateTotal,
        total_paid: totalPaidByProject
      } : null
    };

    // Fetch line items
    const { data: lineItems, error: lineItemsError } = await supabase
      .from('invoice_line_items')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('sort_order', { ascending: true });

    if (lineItemsError) throw lineItemsError;

    // Fetch company data
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('*')
      .eq('id', invoice.company_id)
      .single();

    if (companyError) throw companyError;

    // Fetch user profile
    const { data: userProfile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', invoice.created_by)
      .single();

    if (profileError) throw profileError;

    // Fetch document defaults
    const { data: documentDefaults, error: defaultsError } = await supabase
      .from('document_defaults')
      .select('*')
      .eq('company_id', invoice.company_id)
      .maybeSingle();

    if (defaultsError) {
      console.error('Error fetching document defaults:', defaultsError);
    }

    // Calculate totals
    const subtotal = invoice.subtotal || 0;
    const taxAmount = invoice.tax_amount || 0;
    const totalAmount = invoice.total_amount || 0;

    return {
      invoice: enhancedInvoice,
      lineItems: lineItems || [],
      customer: invoice.customers,
      company,
      userProfile,
      documentDefaults,
      subtotal,
      taxAmount,
      totalAmount,
    };
  } catch (error) {
    console.error('Error fetching invoice export data:', error);
    throw error;
  }
};
